import type { InputHTMLAttributes } from 'react'
import styled, { css } from 'styled-components'

interface InputWrapperProps extends InputHTMLAttributes<HTMLInputElement> {
  withIcon: boolean
  disabled: boolean
  hasError?: boolean
}

interface LabelTextProps {
  isTransitioned: boolean
  withIcon: boolean
  disabled: boolean
}

export const getInputBorderColor = (props: InputWrapperProps & { theme: any }) => {
  if (props.hasError) {
    return props.theme.colors.failure
  }

  return props.theme.colors.grey5
}

export const getInputBackgroundColor = (props: InputWrapperProps & { theme: any }) => {
  if (props.disabled) {
    return props.theme.colors.grey5
  }

  if (props.hasError) {
    return 'var(--color--semantic--error--50)'
  }

  return ''
}

export const InputWrapper = styled.div<InputWrapperProps>`
  display: flex;
  align-items: center;
  height: 48px;
  border: 2px solid ${props => getInputBorderColor(props)};
  border-radius: 8px;
  position: relative;
  background-color: ${props => getInputBackgroundColor(props)};

  &:hover {
    border: 2px solid ${props => (!props.disabled ? props.theme.colors.grey3 : 'transparent')};
  }

  input {
    padding-block: 12px;
    padding-inline: ${props => (props.withIcon ? '8px' : '16px')} 16px;
  }

  svg {
    margin-block: 8px;
    margin-inline: 0 8px;
    fill: ${props => props.theme.colors.grey2};
    flex: 0 0 auto;
  }
`

export const InputElement = styled.input`
  width: 100%;
  font-weight: 700;
  border: none;
  border-radius: 6px;

  &:focus {
    outline: none;
  }

  &:disabled {
    color: ${props => props.theme.colors.grey2};
  }
`

export const IconWrapper = styled.span`
  flex: 0 0 auto;
  text-align: center;
  display: flex;
`

export const Label = styled.label`
  display: inline-block;
  font-family: ${props => props.theme.fonts.families.primary};
  color: ${props => props.theme.colors.grey3};
  font-size: 16px;
  font-weight: 400;
  text-align: start;
  width: 100%;

  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    box-shadow: 0 0 0 30px transparent inset;
    transition: background-color 5000s ease-in-out 0s;
  }
`

const labelWithiconTransitioned = css`
  transform: translate(calc(var(--transform-direction) * -40px), -35px) scale(0.75) !important;
  padding-block: 3px;
  padding-inline: 5px;
`

const labelTransitioned = css`
  transform: translate(calc(var(--transform-direction) * -15px), -35px) scale(0.75) !important;
  padding-block: 3px;
  padding-inline: 5px;
`

const getLabelMaxWidth = ({ withIcon, isTransitioned }: LabelTextProps) => {
  if (isTransitioned) {
    return `calc(100% + ${withIcon ? '80px' : '20px'})`
  }
  return `calc(100% - ${withIcon ? '60px' : '35px'})`
}

const getLabelBackgroundColor = (props: LabelTextProps & { theme: any }) => {
  if (props.isTransitioned) {
    return props.theme.colors.grey8
  }

  return 'transparent'
}

export const LabelText = styled.span<LabelTextProps>`
  white-space: nowrap;
  position: absolute;
  inset-block-start: 50%;
  transform-origin: left;
  inset-inline-start: ${props => (props.withIcon ? '45px' : '20px')};
  transform: ${props =>
    props.withIcon
      ? 'translate(calc(var(--transform-direction) * 0px), -50%)'
      : 'translateY(-50%)'};
  pointer-events: none;
  transition:
    transform 0.1s,
    background-color 0.1s;

  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  max-width: ${props => getLabelMaxWidth(props)};

  ${props =>
    props.isTransitioned && (!props.withIcon ? labelTransitioned : labelWithiconTransitioned)}

  background-color: ${props => getLabelBackgroundColor(props)};
`
